import React from 'react';
import { Link } from 'react-router-dom';
import Header from '../components/Header';
import Footer from '../components/Footer';
import wallpapers from '../data/kirikanWallpapers.json';
import './KirikanMaterials.css';

const themes = {
  chapel: { name: '光のチャペル', en: 'LIGHT IN THE CHAPEL', description: 'やわらかな光と、穏やかなひととき。' },
  sky: { name: '青空の祝福', en: 'UNDER THE BLUE SKY', description: '風に揺れる髪。どこまでも澄んだ空。' },
  night: { name: '星月夜の祈り', en: 'A PRAYER UNDER THE STARS', description: '一日の終わりに、静かな星のきらめきを。' },
};

function WallpaperCard({ wallpaper, index }) {
  const { id, width, height, file, bytes, originalWidth, originalHeight } = wallpaper;
  const theme = themes[id.split('-')[0]];
  const device = id.endsWith('pc') ? 'PC' : 'スマホ';
  return (
    <article className="km-card">
      <a className="km-preview" href={`/downloads/kirikan/${file}`} target="_blank" rel="noopener noreferrer" aria-label={`${theme.name}・${device}用を大きく見る（新しいタブ）`}>
        <img src={`/static/images/kirikan-wallpapers/${id}.jpg`} alt={`${theme.name}のキリ看ちゃん・${device}用壁紙`} width={width} height={height} loading="lazy" decoding="async" />
        <span>大きく見る ↗</span>
      </a>
      <div className="km-card-body">
        <p className="km-card-kicker">0{index + 1} / {theme.en}</p>
        <h3>{theme.name}</h3>
        <p className="km-description">{theme.description}</p>
        <p className="km-meta">{width.toLocaleString()} × {height.toLocaleString()} px · PNG · {(bytes / 1024 / 1024).toFixed(1)} MB</p>
        <a className="km-download" href={`/downloads/kirikan/${file}`} download={`kirikan-${file}`} aria-label={`${theme.name}・${device}用PNGをダウンロード`}>壁紙をダウンロード <span aria-hidden="true">↓</span></a>
        <a className="km-original" href={`/downloads/kirikan/${id}-original.png`} download={`kirikan-${id}-original.png`}>元画像を保存（{originalWidth} × {originalHeight}）</a>
      </div>
    </article>
  );
}

export default function KirikanMaterials() {
  return (
    <div className="body km-body">
      <Header title="キリ看ちゃんフリー素材・無料壁紙 - ASMRキリスト教会" description="キリ看ちゃんのPC用・スマホ用壁紙を各3種類、無料でダウンロード。光のチャペル、青空、星月夜をあなたの画面に。" url="https://www.asmrchurch.com/kirikan-materials" image="https://www.asmrchurch.com/static/images/kirikan-wallpapers/chapel-pc.jpg" />
      <main className="km-page">
        <Link className="km-back" to="/">← HOME</Link>
        <section className="km-hero" aria-labelledby="km-title">
          <img src="/static/images/kirikan-wallpapers/chapel-pc.jpg" alt="光あふれるチャペルのキリ看ちゃん" width="960" height="540" fetchPriority="high" />
          <div className="km-hero-copy">
            <p className="km-eyebrow">KIRIKAN CHAN / WALLPAPER COLLECTION</p>
            <h1 id="km-title">キリ看ちゃん<br /><span>フリー素材</span></h1>
            <p>いつもの画面に、ちょっと祝福を。<br />お気に入りのキリ看ちゃんを、あなたの壁紙に。</p>
            <a href="#km-desktop" className="km-hero-link">壁紙を選ぶ <span aria-hidden="true">↓</span></a>
          </div>
          <span className="km-hero-note">3 SCENES × 2 DEVICES</span>
        </section>
        <nav className="km-nav" aria-label="壁紙の種類"><a href="#km-desktop">PC用 <span>3点</span></a><a href="#km-phone">スマホ用 <span>3点</span></a><a href="#km-guide">保存方法・ご利用について</a></nav>
        <p className="km-intro">登録不要・無料でダウンロード。文字のないイラストで、毎日の画面を楽しんでください。</p>
        {[['pc', 'km-desktop', 'DESKTOP', 'PC用の壁紙', '横長 16:9 ／ 3840 × 2160 px'], ['phone', 'km-phone', 'SMARTPHONE', 'スマホ用の壁紙', '縦長 9:16 ／ 1440 × 2560 px']].map(([device, sectionId, label, title, size]) => (
          <section className={`km-section km-section--${device}`} id={sectionId} key={device} aria-labelledby={`${sectionId}-title`}>
            <header className="km-section-heading"><div><p className="km-eyebrow">{label}</p><h2 id={`${sectionId}-title`}>{title}</h2></div><p>{size}</p></header>
            <div className="km-grid">{wallpapers.filter(w => w.id.endsWith(device)).map((wallpaper, index) => <WallpaperCard key={wallpaper.id} wallpaper={wallpaper} index={index} />)}</div>
          </section>
        ))}
        <section className="km-guide" id="km-guide" aria-labelledby="km-guide-title">
          <p className="km-eyebrow">HOW TO USE</p><h2 id="km-guide-title">保存方法・ご利用について</h2>
          <div className="km-guide-grid"><div><h3>壁紙の保存と設定</h3><p>「壁紙をダウンロード」からPNG画像を保存し、端末の壁紙設定で選んでください。スマホで画像が表示される場合は、長押しや共有メニューから画像を保存できます。</p><p>画面の縦横比によって端が切れることがあります。設定時に位置と拡大率を調整してください。</p></div><div><h3>無料で使える範囲</h3><p>このページで配布する6枚は、ご自身のPC・スマホの壁紙として無料で保存・利用できます。個別の連絡やクレジット表記は不要です。</p><p>その他の用途は<Link to="/kirikan-guideline">キリ看ちゃん利用ガイドライン</Link>をご確認ください。</p></div></div>
          <details className="km-quality"><summary>画像サイズと制作について</summary><p>画像生成AIを使用しています。配布版はPC用3840 × 2160 px、スマホ用1440 × 2560 pxのPNGです。生成元画像（PC用1672 × 941 px／スマホ用941 × 1672 px）をリサイズしたもので、ネイティブ4Kではありません。拡大により元画像以上の細部が増えるものではありません。各カードから加工前のPNGも保存できます。</p></details>
          <p className="km-credit">キリ看ちゃん © ASMRキリスト教会</p>
        </section>
      </main>
      <Footer />
    </div>
  );
}
